import React, { useCallback, useEffect, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { fetchPatternPerformanceBreakdown } from '../../api/adminPatternPerformance';
import {
  BUCKET_TIMEFRAMES,
  DEFAULT_BUCKET_ENGINE_VERSIONS,
  DEFAULT_BUCKET_TIMEFRAMES,
  HISTORICAL_PATTERN_ENGINE_VERSIONS,
  resolvePatternEngineVersionLabel,
} from '../pattern-scanner/scannerExperienceShared';

const PAGE_COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
};

const SCORE_BUCKETS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeRatio(value) {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
}

function formatPercent(value, fallback = 'n/a') {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return fallback;
  }
  return `${(normalized * 100).toFixed(1)}%`;
}

function compareEngineVersions(left, right) {
  if (left === right) {
    return 0;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right));
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 22,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function versionLabel(version) {
  return resolvePatternEngineVersionLabel(version).toUpperCase();
}

const TP_UNAVAILABLE = 'Statistiques TP non disponibles';
const TP_INSUFFICIENT = 'Données insuffisantes';

function describeCoverage(takeProfits) {
  const eligible = Number(takeProfits.eligibleCount) || 0;
  const definitive = Number(takeProfits.definitiveCount) || 0;
  const pending = Number(takeProfits.pendingCount) || 0;
  const unknown = Number(takeProfits.unknownCount) || 0;
  const ambiguous = Number(takeProfits.ambiguousCount) || 0;
  const notCalculated = Number(takeProfits.notCalculatedCount) || 0;
  const coverage = takeProfits.coverageRate;
  const coverageLabel = (coverage === null || coverage === undefined)
    ? `${definitive}/${eligible}`
    : `${definitive}/${eligible} (${formatPercent(coverage)})`;
  return [
    `Couverture : ${coverageLabel}`,
    `Non calculés : ${notCalculated}`,
    `En attente : ${pending}`,
    `Inconnus : ${unknown}`,
    `Ambigus : ${ambiguous}`,
  ].join(' · ');
}

function TakeProfitCell({ takeProfits, rateKey, color }) {
  // Missing metric payload (older backend / benchmark-scoped row) -> never a fabricated 0%.
  if (!takeProfits) {
    return (
      <TableCell sx={{ color: PAGE_COLORS.muted }}>
        <Tooltip title={TP_UNAVAILABLE}>
          <span>—</span>
        </Tooltip>
      </TableCell>
    );
  }

  const rate = takeProfits[rateKey];
  const eligible = Number(takeProfits.eligibleCount) || 0;
  const definitive = Number(takeProfits.definitiveCount) || 0;
  const rateAvailable = rate !== null && rate !== undefined && eligible > 0;

  // No usable rate at all (no eligible cohort or null rate) -> em dash, never 0%.
  if (!rateAvailable) {
    return (
      <TableCell sx={{ color: PAGE_COLORS.muted }}>
        <Tooltip title={TP_INSUFFICIENT}>
          <span>—</span>
        </Tooltip>
      </TableCell>
    );
  }

  // Complete coverage: definitive reading, same formatting as Breakout/Invalidation (0% allowed).
  if (takeProfits.coverageComplete === true) {
    return <TableCell sx={{ color }}>{formatPercent(rate)}</TableCell>;
  }

  // Partial coverage without any definitive result -> cannot present a definitive statistic.
  if (definitive === 0) {
    return (
      <TableCell sx={{ color: PAGE_COLORS.muted }}>
        <Tooltip title={`${TP_INSUFFICIENT} (aucun résultat définitif)`}>
          <span>—</span>
        </Tooltip>
      </TableCell>
    );
  }

  // Partial coverage: show the rate explicitly flagged as provisional, with the coverage detail.
  return (
    <TableCell sx={{ color }}>
      <Tooltip title={describeCoverage(takeProfits)}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{formatPercent(rate)}</span>
          <Chip
            size="small"
            label="provisoire"
            sx={{
              height: 18,
              bgcolor: alpha(PAGE_COLORS.warning, 0.12),
              color: PAGE_COLORS.warning,
              fontWeight: 700,
            }}
          />
        </Stack>
      </Tooltip>
    </TableCell>
  );
}

function ConfigurablePatternPerformanceTable({ appliedFilters = {}, lowSampleThreshold = 30 }) {
  const [showPattern, setShowPattern] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState(DEFAULT_BUCKET_ENGINE_VERSIONS);
  const [selectedTimeframes, setSelectedTimeframes] = useState(DEFAULT_BUCKET_TIMEFRAMES);
  const [selectedScoreBuckets, setSelectedScoreBuckets] = useState([]);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const orderedVersions = [...HISTORICAL_PATTERN_ENGINE_VERSIONS].sort(compareEngineVersions);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requestFilters = { ...appliedFilters };
      // Les sélections locales sont l'autorité pour ce breakdown : le filtre
      // global ne doit pas les neutraliser.
      delete requestFilters.version;
      delete requestFilters.timeframe;
      const payload = await fetchPatternPerformanceBreakdown({
        ...requestFilters,
        groupByPattern: showPattern,
        engineVersions: selectedVersions.join(','),
        timeframes: selectedTimeframes.join(','),
        scoreBuckets: selectedScoreBuckets.join(','),
      });
      setRows(payload?.rows || []);
    } catch (loadError) {
      setRows(null);
      setError(loadError.message || 'Impossible de charger la segmentation par bucket de score.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, showPattern, selectedVersions, selectedTimeframes, selectedScoreBuckets]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const toggleVersion = (version) => (event) => {
    const checked = event.target.checked;
    setSelectedVersions((current) => (
      checked ? [...current, version] : current.filter((item) => item !== version)
    ));
  };

  const toggleTimeframe = (timeframe) => (event) => {
    const checked = event.target.checked;
    setSelectedTimeframes((current) => (
      checked ? [...current, timeframe] : current.filter((item) => item !== timeframe)
    ));
  };

  const toggleScoreBucket = (bucket) => (event) => {
    const checked = event.target.checked;
    setSelectedScoreBuckets((current) => (
      checked ? [...current, bucket] : current.filter((item) => item !== bucket)
    ));
  };

  const showVersionColumn = selectedVersions.length > 0;

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: PAGE_COLORS.ink }} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!rows?.length) {
      return <Alert severity="info">Aucune donnée pour cette sélection.</Alert>;
    }

    return (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showPattern ? <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell> : null}
              {showVersionColumn ? <TableCell sx={{ fontWeight: 800 }}>Version</TableCell> : null}
              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>TP1</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>TP2</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>TP3</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
                hover
              >
                {showPattern ? (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                      {row.pattern}
                    </Typography>
                  </TableCell>
                ) : null}
                {showVersionColumn ? (
                  <TableCell>{String(row.engineVersion || '').toUpperCase()}</TableCell>
                ) : null}
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{row.detections}</Typography>
                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                  </Stack>
                </TableCell>
                <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                <TableCell sx={{ color: PAGE_COLORS.danger }}>{formatPercent(row.invalidationRate)}</TableCell>
                <TakeProfitCell takeProfits={row.takeProfits} rateKey="tp1Rate" color={PAGE_COLORS.success} />
                <TakeProfitCell takeProfits={row.takeProfits} rateKey="tp2Rate" color={PAGE_COLORS.success} />
                <TakeProfitCell takeProfits={row.takeProfits} rateKey="tp3Rate" color={PAGE_COLORS.success} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
              Analyse de performance par Score
            </Typography>
            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
              Segmentation dynamique par pattern et version, croisée avec le bucket de score, filtrable par moteur, timeframe et score.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: alpha(PAGE_COLORS.ink, 0.03),
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Dimensions
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox size="small" checked={showPattern} onChange={(event) => setShowPattern(event.target.checked)} />}
                label="Pattern"
              />
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Versions moteur
            </Typography>
            <FormGroup row>
              {orderedVersions.map((version) => (
                <FormControlLabel
                  key={version}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedVersions.includes(version)}
                      onChange={toggleVersion(version)}
                    />
                  )}
                  label={versionLabel(version)}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Timeframes
            </Typography>
            <FormGroup row>
              {BUCKET_TIMEFRAMES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedTimeframes.includes(option.value)}
                      onChange={toggleTimeframe(option.value)}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Scores
            </Typography>
            <FormGroup row>
              {SCORE_BUCKETS.map((bucket) => (
                <FormControlLabel
                  key={bucket}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedScoreBuckets.includes(bucket)}
                      onChange={toggleScoreBucket(bucket)}
                    />
                  )}
                  label={bucket}
                />
              ))}
            </FormGroup>
          </Stack>
        </Stack>

        {renderBody()}
      </Stack>
    </Paper>
  );
}

export default ConfigurablePatternPerformanceTable;
